import React, { useRef, useState, useCallback, useMemo } from 'react'
import './weekGrid.css'

export interface WeekGridRow {
  key: string
  label?: string
  header?: React.ReactNode
  ariaLabel?: string
}

export interface WeekGridDayColumn {
  key: string
  label?: string
  header?: React.ReactNode
  subHeader?: React.ReactNode
  ariaLabel?: string
}

export interface WeekGridProps {
  /**
   * Rows to display. Can be passed as `rows` or `rowHeaders`.
   */
  rows?: (string | WeekGridRow)[]
  rowHeaders?: (string | WeekGridRow)[]
  /**
   * Exactly 7 day column headers. Can be passed as `columns` or `dayColumns`.
   */
  columns?: (string | WeekGridDayColumn)[]
  dayColumns?: (string | WeekGridDayColumn)[]
  /**
   * Renders the interactive content for cell at `rowKey` and `dayIndex` (0..6).
   */
  renderCell: (rowKey: string, dayIndex: number) => React.ReactNode
  /**
   * Called when cell button is clicked, or activated via Enter / Space keyboard key.
   */
  onCellActivate?: (rowKey: string, dayIndex: number) => void
  /**
   * Required accessible label for the grid.
   */
  ariaLabel: string
  /**
   * Optional header for the corner / row-header column (e.g. "Person").
   */
  cornerHeader?: React.ReactNode
  /**
   * Optional CSS class.
   */
  className?: string
  /**
   * Message shown when rows array is empty.
   */
  emptyMessage?: string
}

interface NormalizedRow {
  key: string
  label: string
  headerNode: React.ReactNode
  ariaLabel?: string
}

interface NormalizedCol {
  key: string
  label: string
  headerNode: React.ReactNode
  subHeaderNode?: React.ReactNode
  ariaLabel?: string
}

export function WeekGrid({
  rows: rowsProp,
  rowHeaders: rowHeadersProp,
  columns: columnsProp,
  dayColumns: dayColumnsProp,
  renderCell,
  onCellActivate,
  ariaLabel,
  cornerHeader,
  className = '',
  emptyMessage,
}: WeekGridProps): React.ReactElement {
  const rows: NormalizedRow[] = useMemo(() => {
    const rawRows = rowsProp ?? rowHeadersProp ?? []
    return rawRows.map((r, idx) => {
      if (typeof r === 'string') {
        return { key: r || `row-${idx}`, label: r, headerNode: r }
      }
      const label = r.label ?? (typeof r.header === 'string' ? r.header : r.key)
      return {
        key: r.key || `row-${idx}`,
        label,
        headerNode: r.header ?? label,
        ariaLabel: r.ariaLabel,
      }
    })
  }, [rowsProp, rowHeadersProp])

  const columns: NormalizedCol[] = useMemo(() => {
    const rawCols = columnsProp ?? dayColumnsProp ?? []
    return rawCols.map((c, idx) => {
      if (typeof c === 'string') {
        return { key: c || `col-${idx}`, label: c, headerNode: c }
      }
      const label = c.label ?? (typeof c.header === 'string' ? c.header : c.key)
      return {
        key: c.key || `col-${idx}`,
        label,
        headerNode: c.header ?? label,
        subHeaderNode: c.subHeader,
        ariaLabel: c.ariaLabel,
      }
    })
  }, [columnsProp, dayColumnsProp])

  // Track focused cell coordinates [rowIdx, colIdx] for roving tabindex
  const [focusedCell, setFocusedCell] = useState<[number, number]>([0, 0])
  const buttonRefs = useRef<Map<string, HTMLButtonElement | null>>(new Map())

  const focusCell = useCallback(
    (rIdx: number, cIdx: number) => {
      if (rIdx < 0 || rIdx >= rows.length || cIdx < 0 || cIdx >= columns.length) {
        return
      }
      setFocusedCell([rIdx, cIdx])
      const key = `${rIdx}:${cIdx}`
      const btn = buttonRefs.current.get(key)
      btn?.focus()
    },
    [rows.length, columns.length],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, rIdx: number, cIdx: number) => {
      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault()
          if (cIdx < columns.length - 1) focusCell(rIdx, cIdx + 1)
          break
        case 'ArrowLeft':
          e.preventDefault()
          if (cIdx > 0) focusCell(rIdx, cIdx - 1)
          break
        case 'ArrowDown':
          e.preventDefault()
          if (rIdx < rows.length - 1) focusCell(rIdx + 1, cIdx)
          break
        case 'ArrowUp':
          e.preventDefault()
          if (rIdx > 0) focusCell(rIdx - 1, cIdx)
          break
        case 'Home':
          e.preventDefault()
          focusCell(rIdx, 0)
          break
        case 'End':
          e.preventDefault()
          focusCell(rIdx, columns.length - 1)
          break
        case 'Enter':
        case ' ':
          e.preventDefault()
          onCellActivate?.(rows[rIdx].key, cIdx)
          break
        default:
          break
      }
    },
    [columns.length, rows, focusCell, onCellActivate],
  )

  return (
    <div className={`weekGrid__box ${className}`.trim()}>
      <table
        role="grid"
        aria-label={ariaLabel}
        className="weekGrid"
      >
        <thead className="weekGrid__thead">
          <tr role="row">
            <th
              role="columnheader"
              scope="col"
              className="weekGrid__cornerHeader"
            >
              {cornerHeader ?? <span className="visually-hidden">Row</span>}
            </th>
            {columns.map((col) => (
              <th
                key={col.key}
                role="columnheader"
                scope="col"
                aria-label={col.ariaLabel ?? col.label}
                className="weekGrid__colHeader"
              >
                <div className="weekGrid__colTitle">{col.headerNode}</div>
                {col.subHeaderNode && (
                  <div className="weekGrid__colSub">{col.subHeaderNode}</div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr role="row">
              <td
                colSpan={columns.length + 1}
                className="weekGrid__empty"
              >
                {emptyMessage ?? 'No rows to display'}
              </td>
            </tr>
          ) : (
            rows.map((row, rIdx) => (
              <tr
                key={row.key}
                role="row"
                className="weekGrid__row"
              >
                <th
                  role="rowheader"
                  scope="row"
                  aria-label={row.ariaLabel ?? row.label}
                  className="weekGrid__rowHeader"
                >
                  {row.headerNode}
                </th>
                {columns.map((col, cIdx) => {
                  const isFocused =
                    focusedCell[0] === rIdx && focusedCell[1] === cIdx
                  const cellKey = `${rIdx}:${cIdx}`
                  const cellLabel = `${row.label}, ${col.label}`

                  return (
                    <td
                      key={col.key}
                      role="gridcell"
                      className="weekGrid__cell"
                    >
                      <button
                        ref={(el) => {
                          if (el) {
                            buttonRefs.current.set(cellKey, el)
                          } else {
                            buttonRefs.current.delete(cellKey)
                          }
                        }}
                        type="button"
                        className="weekGrid__cellBtn"
                        tabIndex={isFocused ? 0 : -1}
                        aria-label={cellLabel}
                        onClick={() => {
                          setFocusedCell([rIdx, cIdx])
                          onCellActivate?.(row.key, cIdx)
                        }}
                        onFocus={() => setFocusedCell([rIdx, cIdx])}
                        onKeyDown={(e) => handleKeyDown(e, rIdx, cIdx)}
                      >
                        {renderCell(row.key, cIdx)}
                      </button>
                    </td>
                  )
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}
